/* eslint-disable @typescript-eslint/ban-ts-comment */

import { safeJSONParse } from '@shared/utils'
import { request } from '@remote/api/request'
import global from '@remote/store/constantStore'
import { store } from '@remote/store'
import { getFileIconUrl, FloderIcon, BTFloderIcon } from '@remote/utils/file-helper'
import { isHeziNative, isAndroidHezi, isIOSHezi } from '@remote/utils/platform'
import heziBridge, { TAndroidBridge, TIOSBridge } from '@remote/utils/jsbridge/hezi'
import { querys2json, delay, onceExecuteAtATime } from '@remote/utils/base'
import { getDeviceInfo } from '../api-http/index'
import type { IErrorRes, IOptions } from '@remote/type'
import type { ITorrentFile, ITreedTorrentNode, ITreedTorrentFile, ITreedTorrentDir } from './../type'
import type { IMQTTRes, IMQTTExtraParams } from '../type'

export function normalizeUrl (url: string, host = ''): string {
  return (url.startsWith('http') ? url : host + url).replace(/([^:]\/)\/+/g, '$1')
}

export function guid (): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0,
      v = c == 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export function getDeviceId (): string {
  return global.fromSpace
}

export const isMagnetTypeBTLink = (link: string) => /magnet:\?xt=urn:btih:[0-9a-zA-Z]{30,}.*/.test(link)
export const isOtherTypeBTLink = (link: string) => /(thunder|http|https|ed2k|ftp|xdrive):\/\/[\S]*/.test(link)
export const isXlShareLink = (link: string) => /【迅雷口令#X(\w+)#】(.*提取密?码\W*(\w+))?/.test(link)
export const isXlCloudShareLink = (link: string) => /(https?:\/\/pan\.xunlei\.com\/s\/([0-9a-zA-Z_-]+)(\?[^#\s]*)?(#\S*)?)(\s*提取密?码\W*(\w+))?/.test(link)
export const getMagnetDownloadId = (link: string) => link.replace(/magnet:\?xt=urn:btih:/, '')

// 提取迅雷口令信息
export function extractXlShareLinkInfo (link: string) {
  if (!isXlShareLink(link)) return { id: '', password: '' }
  return {
    id: RegExp.$1,
    password: RegExp.$3,
  }
}

// 提取迅雷分享链接信息
export function extractXlCloudShareLinkInfo (link: string) {
  if (!isXlCloudShareLink(link)) return { id: '', link, password: '' }
  const url = RegExp.$1
  const shareId = RegExp.$2
  const search = RegExp.$3
  const password = RegExp.$6
  const querys = querys2json(search)
  const info = {
    id: shareId,
    link: /pwd=/.test(url) ? url : url + `?pwd=${(password || querys.pwd)}#`,
    password: password || querys.pwd,
  }

  return info
}

const removeRepetBtLink = (links: string[]): string[] => {
  const map: { [link: string]: boolean } = {}
  return links.reduce((result: string[], link: string) => {
    if (!map[link]) {
      result.push(link)
      map[link] = true
    }
    return result
  }, [])
}

export const matchBatchBtLink = (links: string, removeRepet?: boolean): [string[], string[]] => {
  try {
    const joinLineBreak = (magnet) => ` ${magnet}`
    const magnets = links
      .replace(/magnet:\?xt=urn:btih/g, joinLineBreak)
      .match(/magnet:\?xt=urn:btih:[0-9a-zA-Z]{30,}[^(magnet|&|「|」)|0-9a-zA-Z]*/g) || []
    const downloadUrls = links.match(/(thunder|http|https|ed2k|ftp|xdrive):\/\/[\S]*/g) || []
    const cn = /[\u4E00-\u9FA5]|[\uFE30-\uFFA0]|\s/g
    const _magnets = magnets?.map(item => item.replace(cn, '')) as string[]
    return [
      removeRepet ? removeRepetBtLink(_magnets) : _magnets,
      removeRepet ? removeRepetBtLink(downloadUrls) : downloadUrls,
    ]
  } catch (err) {
    return [[links], []]
  }
}

export function readAsText (file: Blob) {
  return new Promise<string>(function resolve (resolve) {
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = function onload (e: ProgressEvent<FileReader>) {
      resolve(e?.target?.result as string)
    }
  })
}

export async function sendMessage<T = any> (method: string, param: { [key: string]: any } = {}, options?: IOptions & { apiEnv?: 'heziDevelopment' }): Promise<[IErrorRes, T]> {
  const url = '/drive/v1/resource/list'
  const reqParams = {
    type: 'call',
    call_id: guid(),
    data: {
      method: method,
      param: param,
    },
  }

  const reqOptions = Object.assign({
    method: 'POST',
    apiEnv: 'heziDevelopment',
  }, options)
  reqOptions['headers'] = Object.assign({}, reqOptions['headers'], { Target: global.fromSpace })

  const [err, res]: [IErrorRes, T] = await request(url, reqParams, reqOptions)
  if (err || !res) {
    return [err, res]
  } else {
    return [err, res]
  }
}

export function parseMqttResult<T = any> (response, passCodes = [0]) {
  const [err, ret] = response
  const data = safeJSONParse(ret?.data) as IMQTTRes<T>
  if (!err && data && passCodes.includes(data.ret)) {
    return data
  } else {
    throw err?.error_description || data.msg
  }
}

/** 
 * 获取文件列表的公共目录
 * 如 [
 *  { file_path: '/xos_storage/v1/BA3C-DFEA/我的下载/72-1-64/A.txt' },
 *  { file_path: '/xos_storage/v1/BA3C-DFEA/我的下载/72-1-64/B.txt' },
 * ]
 * => '/xos_storage/v1/BA3C-DFEA/我的下载/72-1-64'
 */
export function getCommonDirPath (files: ITorrentFile[]) {
  if (files.length === 1) {
    const [file] = files
    const { file_path } = file
    return file_path.split('/').slice(0, -1).join('/')
  }
  const fairRuler = files.reduce<string[]>((ruler, file: ITorrentFile) => {
    const { file_path } = file
    const crumbs = file_path.split('/')
    if (!ruler.length) return crumbs
    const fairRuler: string[] = []
    for (let i = 0, j = 0; i < ruler.length && j < crumbs.length; ++i, ++j) {
      const pathA = ruler[i]
      const pathB = crumbs[j]
      // 即公共目录
      if (pathA === pathB) {
        fairRuler.push(pathA)
      }
    }
    return fairRuler
  }, [])

  return fairRuler.join('/')
}

export function sortTreeByNodeType (tree: ITreedTorrentNode[]) {
  if (!Array.isArray(tree)) return
  tree.sort((nodeA, nodeB) => {
    if (nodeA.type > nodeB.type) return 1
    if (nodeA.type < nodeB.type) return -1
    if (nodeA.name > nodeB.name) return 1
    return -1
  })
  for (let i = 0; i < tree.length; ++i) {
    const node = tree[i]
    if (Array.isArray(node.children)) {
      sortTreeByNodeType(node.children)
    }
  }
}

/** 将扁平的文件列表根据file_path生成树型文件目录 */
export function buildFileListTree (list: ITorrentFile[], showAll = false, url?: string, btName = ''):ITreedTorrentNode[] {
  const startTime = Date.now()
  // 仅展示可下载文件
  const files = showAll ? list.slice() : list.filter(f => !f.is_download)
  const root = getCommonDirPath(files)
  const uuid = guid()
  let id = 0
  const tree: ITreedTorrentNode[] = [{
    type: 'dir',
    size: 0,
    file_size: 0,
    name: btName || root.replace(/.*\//g, ''),
    path: root,
    children: [],
    meta: { url },
    icon: BTFloderIcon,
    id: `${uuid}.${id}`,
    checkType: 0,
    title: btName || root.replace(/.*\//g, ''),
    count: 0,
    isDir: true,
    ext:'文件夹',
  }]

  if (!root) return tree

  const mkdirp = (dir: string, checkType = 0) => {
    const dirnames = dir.split('/')
    let [node] = tree
    while (dirnames.length) {
      const dirname = dirnames.shift()
      if (!dirname) continue
      const isDir = node.type === 'dir'
      if (!isDir) continue

      if (Array.isArray(node.children)) {
        const target = node.children.find(d => d.name === dirname)
        if (target) {
          node = target
          continue
        }
      }
      id += 1
      const target: ITreedTorrentDir = {
        type: 'dir',
        size: 0,
        file_size: 0,
        name: dirname,
        path: `${node.path}/${dirname}`,
        children: [],
        meta: { url },
        icon: FloderIcon,
        id: `${uuid}.${id}`,
        checkType: checkType,
        title: dirname,
        count: 0,
        isDir: true,
        ext: '文件夹',
        // is_download: isDownload,
      }
      if (Array.isArray(node.children)) {
        node.children.push(target)
      } else {
        node.children = [target]
      }
      node = target
    }
  }

  files.forEach((file) => {
    const { file_size, file_path } = file
    // @ts-ignore
    const file_name = (file.file_name || file.name) as string
    const private_path = file_path.replace(`${root}/`, '')
    id += 1
    if (file_name === private_path) {
      const [node] = tree
      // 没有其他目录
      const targetFile: ITreedTorrentFile = {
        type: 'file',
        name: file_name,
        size: +file_size,
        path: file_path,
        meta: { url },
        icon: getFileIconUrl(file_name),
        file_size: +file_size,
        file_index: file.index,
        file,
        id: `${uuid}.${id}`,
        checkType: 0,
        title: file_name,
        count: 0,
        isDir: false,
        ext: file_name.split('.').pop(),
        is_download: file.is_download,
      }
      node.size += +file_size
      node.children?.push(targetFile)
    } else {
      const dir_path = private_path.replace(/\/[^/]+$/, '')
      mkdirp(dir_path)
      let [node] = tree
      node.size += +file_size
      const dirs = dir_path?.split('/')

      // 目录大小统计
      for (let i = 0; i < dirs.length && node; ++i) {
        const dirname = dirs[i]
        if (!dirname) break
        if (!Array.isArray(node.children)) break
        const target = node.children.find(d => d.name === dirname)
        if (!target) break
        node = target
        node.size += +file_size
      }

      // 定位到文件最终所在目录
      if (node) {
        const targetFile: ITreedTorrentFile = {
          type: 'file',
          name: file_name,
          size: +file_size,
          path: file_path,
          meta: { url },
          icon: getFileIconUrl(file_name),
          file_size: +file_size,
          file_index: file.index,
          file,
          id: `${uuid}.${id}`,
          checkType: 0,
          title: file_name,
          count: 0,
          isDir: false,
          ext: file_name.split('.').pop(),
          is_download: file.is_download,
        }
        if (Array.isArray(node.children)) {
          node.children.push(targetFile)
        } else {
          node.children = [targetFile]
        }
      }
    }
  })

  sortTreeByNodeType(tree)

  const endTime = Date.now()
  console.log('[buildFileListTree]耗时:', endTime - startTime)
  return tree
}

export function findTreeNode (tree: ITreedTorrentNode[], path: string): ITreedTorrentNode | null {
  if (!Array.isArray(tree)) return null
  if (tree.length === 0) return null
  const subTree: ITreedTorrentNode[] = []
  for (let i = 0; i < tree.length; ++i) {
    const node = tree[i]
    if (node.path === path) return node
    if (path.startsWith(node.path)) subTree.push(node)
  }
  return subTree.reduce<ITreedTorrentNode | null>((result, node) => {
    if (result) return result
    if (!node.children) return null
    return findTreeNode(node.children, path)
  }, null)
}

type IPathResolve = (...arg: (string | string[])[]) => string

export const pathResolve: IPathResolve = (...arg) =>
  ('/' + arg?.flat()?.join('/'))
    ?.replace(/(\/)+/gm, '/')
    ?.replace(/(\/)$/gm, '')
    ?.replace(/^(\w).+/, (p) => `/${p}`)
    ?.replace(/(^\s*)|(\s*$)/g, '')


interface IMQTTRequestOptions extends IOptions {
  apiEnv?: 'heziDevelopment'
  /** 若为true，优先使用客户端处理MQTT消息的收发 */
  native?: boolean
}
interface IMQTTResponse<T = any> {
  data: T
  msg?: string
}
export async function sendMQTTMessage<T = any> (
  appId: 'xl_shell' | 'xl_gx' | 'xl_disk' | 'xos',
  method: string,
  param: { [key: string]: any } = {},
  options?: IMQTTRequestOptions,
): Promise<[IErrorRes, IMQTTResponse<T>]> {
  const nativeProxy = !!options?.native
  if (nativeProxy && isHeziNative) {
    if (isAndroidHezi || isIOSHezi) {
      return new Promise((resolve, reject) => {
        if (!heziBridge) return reject(new Error('请使用盒子客户端'))
        if (isAndroidHezi) {
          console.log(`[MQTT][postMQTTMessage][request][${appId}.${method}]:`, param)
          const params = JSON.stringify({
            appId,
            method,
            params: param,
          });
          (heziBridge as TAndroidBridge).callHandler('postMQTTMessage', params, (err, response) => {
            console.log(`[MQTT][postMQTTMessage][response][${appId}.${method}]:`, err, response)
            resolve([err as IErrorRes, { data: JSON.stringify(response) as unknown as T }])
          })
        } else {
          console.log(`[MQTT][postMQTTMessage][request][${appId}.${method}]:`, param)
          const params = {
            appid: appId,
            method,
            params: param,
          };
          (heziBridge as TIOSBridge).callHandler('postMQTTMessage', params, (err, response) => {
            console.log(`[MQTT][postMQTTMessage][response][${appId}.${method}]:`, err, response)
            resolve([err as any, { data: JSON.stringify(response?.data) as unknown as T }])
          })
        }
      })
    }
  }

  const url = `/drive/v1/mqtt?app_id=${appId}`
  const reqParams = {
    app_id: appId,
    type: 'call',
    call_id: guid(),
    data: {
      method: method,
      param: param,
    },
  }

  const reqOptions = Object.assign({
    method: 'POST',
    apiEnv: 'heziDevelopment',
  }, options)
  reqOptions['headers'] = Object.assign({}, reqOptions['headers'], { Target: global.fromSpace })

  const [err, res]: [IErrorRes, IMQTTResponse<T>] = await request(url, reqParams, reqOptions)
  if (err || !res) {
    return [err, res]
  } else {
    return [err, res]
  }
}


const localStore = {
  cookie: '',
}

const getCurrentDeviceInfo = onceExecuteAtATime(getDeviceInfo)
export async function getCooikeAsync () {
  const dftDeviceId = global.device?.deviceId
  const deviceId = store.state.curDeviceRunner.params?.target || dftDeviceId
  if (!deviceId) return ''
  if (localStore.cookie) return localStore.cookie
  return getCurrentDeviceInfo(deviceId).then(device => {
    if (!device) return ''
    localStore.cookie = device.cookie
    setTimeout(() => {
      localStore.cookie = ''
    }, 30 * 1000)
    return device.cookie
  })
}

type TMethod =
  | 'reboot'
  | 'halt'
  | 'ping'
  // 获取设备详情（包含CPU、内存等动态详情），仅为了获取版本号、publish_type等固定数据优先使用`device_getinfo_base`方法
  | 'device_getinfo'
  // 快速获取基础的设备详情数据（xos_v1119以上生效），失败使用device_getinfo进行降级处理
  | 'device_getinfo_base'
  | 'app_getstatus'
  | 'update_all_to_last'
  | 'update_app_to_last'
  | 'udapte_to_last'
  // 修改系统环境变量（xos_v1119以上生效）
  | 'set_publish_type'
  // 获取第三方授权信息（xos_v1139以上生效）
  | 'auth_provider'
  // 将第三方授权码传给设备以换取授权token
  | 'authorize'
  // 获取第三方云盘文件列表
  | 'pan_get_file_list'
  // 获取第三方云盘文件详情
  | 'pan_get_file_metas'
  // 迅雷云盘接口服务端代理
  | 'drive_common'
  | 'get_album_from'
  | 'del_album_from'
  | 'set_album_from'
  | 'device_setinfo'

export async function sendXOSMessage<T = any> (method: TMethod, params?: { [key: string]: any }, ext?: IMQTTExtraParams):Promise<IMQTTRes<T>> {
  const _params = Object.assign({}, params)
  if (!_params.cookie) {
    const cookie = await getCooikeAsync()
    _params.cookie = cookie
  }

  const options = ext?.native ? { native: true } : {}
  const promise = sendMQTTMessage<T>('xos', method, _params, options).then(parseMqttResult)
  if (ext?.timeout) {
    const time = ext.timeout
    const onTimeout = async () => {
      await delay(time)
      throw new Error('timeout')
    }
    return Promise.race([promise, onTimeout()])
  }

  return promise
}